import React, { useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useAuth } from '~/context/AuthProvider';
import { createContactAttempt, ContactAttemptCreate } from '~/services/contactAttempts';

interface Props {
  clientName?: string;
  campaignRequestId?: number;
  onAttemptAdded?: () => void;
  onClose: () => void;
}

interface FormValues {
  attemptDate: string;
  methodOfAttempt: 'call' | 'email' | 'both' | '';
  note: string;
}

const AddContactAttemptInlineForm: React.FC<Props> = ({ 
  clientName = 'Client', 
  campaignRequestId,
  onAttemptAdded,
  onClose 
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { person } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      attemptDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
      methodOfAttempt: '',
      note: '',
    },
  });

  const handleClose = () => {
    setError(null);
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!campaignRequestId) {
      setError('Campaign request ID is required');
      return;
    }

    if (!person?.id) {
      setError('User information is not available');
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      // Convert form data to API format
      const contactAttemptData: ContactAttemptCreate = {
        campaign_request: campaignRequestId,
        attempt_datetime: new Date(data.attemptDate + 'T' + new Date().toTimeString().slice(0, 8)).toISOString(),
        method: data.methodOfAttempt as 'call' | 'email' | 'both',
        note: data.note,
        person: person.id,
      };

      await createContactAttempt(contactAttemptData);
      
      // Notify parent component to refresh the list
      onAttemptAdded?.();
      
      handleClose();
    } catch (error: any) {
      console.error('Error saving contact attempt:', error);
      setError(error.message || 'Failed to save contact attempt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='rounded-lg border-2 border-gray-200 bg-gray-50 p-3'>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Error Message */}
        {error && (
          <div className='mb-2 rounded-lg bg-red-50 p-2 text-sm text-red-700'>
            {error}
          </div>
        )}

        {/* Attempt Date */}
        <div className='mb-2'>
          <label htmlFor='attempt-date' className='block text-xs font-semibold'>
            Attempt Date<span className='text-red'>*</span>
          </label>
          <Controller
            name='attemptDate'
            control={control}
            rules={{ required: 'Attempt date is required' }}
            render={({ field }) => (
              <input
                id='attempt-date'
                type='date'
                className='w-full rounded-md border border-black bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none'
                {...field}
              />
            )}
          />
          {errors.attemptDate && (
            <p className='mt-1 text-xs text-red'>{errors.attemptDate.message}</p>
          )}
        </div>

        {/* Method of Attempt */}
        <div className='mb-2'>
          <label className='block text-xs font-semibold'>
            Method of Attempt<span className='text-red'>*</span>
          </label>
          <div className='flex gap-4'>
            <Controller
              name='methodOfAttempt'
              control={control}
              rules={{ required: 'Please select a method of attempt' }}
              render={({ field }) => (
                <>
                  <label className='flex items-center text-sm'>
                    <input
                      type='radio'
                      value='call'
                      checked={field.value === 'call'}
                      onChange={() => field.onChange('call')}
                      className='mr-2'
                    />
                    Call
                  </label>
                  <label className='flex items-center text-sm'>
                    <input
                      type='radio'
                      value='email'
                      checked={field.value === 'email'}
                      onChange={() => field.onChange('email')}
                      className='mr-2'
                    />
                    Email
                  </label>
                  <label className='flex items-center text-sm'>
                    <input
                      type='radio'
                      value='both'
                      checked={field.value === 'both'}
                      onChange={() => field.onChange('both')}
                      className='mr-2'
                    />
                    Both
                  </label>
                </>
              )}
            />
          </div>
          {errors.methodOfAttempt && (
            <p className='mt-1 text-xs text-red'>{errors.methodOfAttempt.message}</p>
          )}
        </div>

        {/* Note Section */}
        <div className='mb-2'>
          <label htmlFor='note' className='block text-xs font-semibold'>
            Note
          </label>
          <Controller
            name='note'
            control={control}
            render={({ field }) => (
              <textarea
                id='note'
                rows={3}
                className='w-full rounded-md border border-black bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none'
                placeholder='Add any notes about this contact attempt...'
                {...field}
              />
            )}
          />
        </div>

        {/* Buttons */}
        <div className='flex justify-start space-x-2'>
          <button
            type='button'
            onClick={handleClose}
            className='rounded-full border-[2px] px-[20px] py-[6px] text-[13px]/[16px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={saving}
            className='rounded-full bg-warning px-[20px] py-[6px] text-[13px]/[16px] font-bold uppercase text-black transition-colors duration-500 hover:bg-yellow disabled:opacity-50'
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddContactAttemptInlineForm;

